import type { LandingLocale } from '../../shared/content/landingContent.js';
import type { LandingEditorProject } from '../../shared/editor/landingEditorTypes.js';
import { rebaseInlineTextStyles } from '../../shared/editor/landingEditorInlineTypography.js';
import type { LandingEditorInlineTextStyle } from '../../shared/editor/landingEditorInlineTypography.js';
import { getValueAtPath, setValueAtPath, type LandingEditorElementDefinition } from './landingEditorRegistry.js';

export interface InlineTextDraft {
  elementId: string;
  value: string;
  /** Session-only styles, used for an unconfirmed multilingual WYSIWYG candidate. */
  styles?: LandingEditorInlineTextStyle[];
  editorKey?: string;
}

type TextDefinition = LandingEditorElementDefinition & { path: string };
type FieldSource = (project: LandingEditorProject, definition: LandingEditorElementDefinition) => Record<string, unknown>;

export class InlineTextCommitResolver {
  resolve(
    draft: InlineTextDraft | null,
    definitions: LandingEditorElementDefinition[],
    currentValue: (definition: TextDefinition) => string,
    normalize: (value: string) => string,
  ): { definition: TextDefinition; value: string } | null {
    if (!draft) return null;
    const definition = definitions.find(candidate => candidate.id === draft.elementId);
    if (!definition?.path || definition.kind !== 'text') return null;

    const textDefinition = definition as TextDefinition;
    const value = normalize(draft.value);
    return value === currentValue(textDefinition) ? null : { definition: textDefinition, value };
  }
}

export const inlineTextCommitResolver = new InlineTextCommitResolver();

/**
 * Flushes the actual textarea value into the exact project snapshot that is about to be mutated.
 * This prevents a click following blur from applying an older render closure over the text commit.
 */
export const mergeInlineTextDraftIntoProject = (
  project: LandingEditorProject,
  draft: InlineTextDraft | null,
  definitions: LandingEditorElementDefinition[],
  locale: LandingLocale,
  fieldSource: FieldSource,
  normalize: (value: string) => string,
) => {
  if (!draft) return false;
  const definition = definitions.find(candidate => candidate.id === draft.elementId);
  if (!definition?.path || definition.kind !== 'text') return false;
  const textDefinition = definition as TextDefinition;
  const source = fieldSource(project, textDefinition);
  const previousValue = getValueAtPath(source, textDefinition.path);
  const value = normalize(draft.value);
  const frame = project.elementFrames[draft.elementId];
  const styles = frame?.inlineTextStyles?.[locale] ?? [];
  if (value === previousValue && (!styles.length || frame?.inlineTextSources?.[locale] === value)) return false;
  setValueAtPath(source, textDefinition.path, value);
  if (!frame || !styles.length) return value !== previousValue;

  const previousText = frame.inlineTextSources?.[locale] ?? previousValue;
  const rebased = rebaseInlineTextStyles(styles, previousText, value);
  const stylesByLocale = { ...frame.inlineTextStyles };
  const sourcesByLocale = { ...frame.inlineTextSources };
  if (rebased.length) {
    stylesByLocale[locale] = rebased;
    sourcesByLocale[locale] = value;
  } else {
    delete stylesByLocale[locale];
    delete sourcesByLocale[locale];
  }
  project.elementFrames[draft.elementId] = {
    ...frame,
    inlineTextStyles: Object.keys(stylesByLocale).length ? stylesByLocale : undefined,
    inlineTextSources: Object.keys(sourcesByLocale).length ? sourcesByLocale : undefined,
  };
  return true;
};
